import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

/**
 * The empty state for every list in the app (spec R6.4, R6.7).
 *
 * The action slot is a slot, not a button prop: only some empty states offer a
 * create action, and in Phase 2 that depends on permissions. Callers pass the
 * shadcn `Button` (or a dialog trigger wrapping one) they already have.
 */
export interface EmptyStateProps {
  /** Optional lucide icon, rendered muted above the title. */
  icon?: LucideIcon;
  /** One short line naming what is missing. */
  title: string;
  /** Optional sentence explaining how to fill it. */
  description?: string;
  /** Optional call to action — typically a `Button` or a dialog trigger. */
  action?: ReactNode;
  className?: string;
}

export const EmptyState = ({
  icon: Icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps) => (
  <Card className={cn('border-dashed bg-transparent shadow-none', className)}>
    <CardContent className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      {Icon ? (
        <Icon
          aria-hidden="true"
          className="size-6 text-muted-foreground"
          strokeWidth={1.5}
        />
      ) : null}
      <p className="text-base font-medium text-foreground">{title}</p>
      {description ? (
        <p className="max-w-prose text-sm text-muted-foreground">
          {description}
        </p>
      ) : null}
      {action ? <div className="pt-2">{action}</div> : null}
    </CardContent>
  </Card>
);
